import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Top-nav bell. Polls `/api/notifications` every 30s so it lights up when
 * someone files a leave or you are assigned as cover — no email needed.
 * The dropdown lists the latest 15 items; clicking one marks it read and
 * navigates to its `link`.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  // click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markOne = async (id) => {
    try {
      await api.post("/notifications/mark-read", { id });
      load();
    } catch { /* ignore */ }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 border border-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 bg-[#FF3E00] text-white text-[9px] font-bold flex items-center justify-center rounded-full"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 border border-zinc-950 bg-white shadow-2xl z-50"
          data-testid="notification-menu"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-950 bg-zinc-50">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-[10px] font-mono uppercase text-zinc-500 hover:text-zinc-950 flex items-center gap-1"
                data-testid="notification-mark-all-btn"
              >
                <CheckCircle size={11} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs font-mono text-zinc-400" data-testid="notification-empty">
                YOU&apos;RE ALL CAUGHT UP
              </div>
            ) : (
              items.map((n) => (
                <NotificationItem key={n.id} n={n} onClick={() => markOne(n.id)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationItem({ n, onClick }) {
  const content = (
    <div
      className={`px-3 py-2.5 border-b border-zinc-100 hover:bg-zinc-50 ${!n.read ? "bg-amber-50/40" : ""}`}
      data-testid={`notification-item-${n.id}`}
    >
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${n.read ? "bg-transparent" : "bg-[#FF3E00]"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{n.title}</div>
          {n.body && <div className="text-xs text-zinc-600 truncate">{n.body}</div>}
          <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
            {n.kind?.replace("hr.", "").replace("_", " ").toUpperCase()} · {n.created_at ? new Date(n.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
          </div>
        </div>
      </div>
    </div>
  );
  if (n.link) {
    return (
      <Link to={n.link} onClick={onClick} className="block text-zinc-950 no-underline">
        {content}
      </Link>
    );
  }
  return <button type="button" onClick={onClick} className="block w-full text-left">{content}</button>;
}
